import React, { useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { SafetyActions } from "../components/SafetyActions";

const badge = {
  pending: "text-amber-400 border-amber-400/40",
  confirmed: "text-emerald-400 border-emerald-400/40",
  completed: "text-sky-400 border-sky-400/40",
  cancelled: "text-red-400 border-red-400/40",
};

const RideCard = ({ r, onRebook, phone }) => (
  <div className="border border-white/10 bg-[var(--nl-bg-2)] p-5" data-testid={`ride-${r.id}`}>
    <div className="flex items-start justify-between gap-4">
      <div>
        <p className="font-serif text-xl">{r.date} · {r.time}</p>
        <p className="text-white/50 font-body text-sm">{r.vehicle} {r.mode === "hourly" ? `· ${r.hours}h` : ""}</p>
      </div>
      <span className={`border px-2 py-0.5 label text-[10px] ${badge[r.status] || "text-white/50 border-white/20"}`}>{r.status}</span>
    </div>
    <div className="mt-3 font-body text-sm text-white/70 border-t border-white/10 pt-3 space-y-1">
      <p><span className="text-gold">↑</span> {r.pickup}</p>
      <p><span className="text-gold">↓</span> {r.dropoff}</p>
      {r.amount != null && <p className="text-white/40 text-xs">Fare hold: ${r.amount}{r.is_deposit ? " (deposit)" : ""}</p>}
    </div>
    <button
      onClick={() => onRebook(r)}
      className="mt-4 label text-gold border border-gold/40 px-4 py-2 hover:bg-gold/10 transition-colors duration-300"
      data-testid={`rebook-${r.id}`}
    >
      Rebook this ride →
    </button>
    <SafetyActions idPayload={{ role: "rider", booking_id: r.id, phone }} reportKind="lost" showSOS={false} testPrefix={`ride-${r.id}`} />
  </div>
);

export default function RideHistory() {
  const navigate = useNavigate();
  const [phone, setPhone] = useState(localStorage.getItem("nl_client_phone") || "");
  const [rides, setRides] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchRides = useCallback(async (p) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/rides`, { params: { phone: p } });
      setRides(data);
      localStorage.setItem("nl_client_phone", p);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load rides");
    } finally {
      setLoading(false);
    }
  }, []);

  const rebook = useCallback((r) => {
    sessionStorage.setItem("nl_rebook", JSON.stringify({
      name: r.name || "", phone: r.phone || "", service: r.service,
      vehicle: r.vehicle, pickup: r.pickup, dropoff: r.dropoff,
      pickup_lat: r.pickup_lat, pickup_lng: r.pickup_lng,
      dropoff_lat: r.dropoff_lat, dropoff_lng: r.dropoff_lng,
      passengers: r.passengers || 1, mode: r.mode || "point_to_point", hours: r.hours || 2,
    }));
    toast.success("Details loaded — just pick a new date & time");
    navigate("/#book");
  }, [navigate]);

  const submit = (e) => { e.preventDefault(); if (phone.trim()) fetchRides(phone.trim()); };

  const today = new Date().toLocaleDateString("en-CA", { timeZone: "America/Toronto" });
  const upcoming = (rides || []).filter((r) => r.date >= today && r.status !== "cancelled");
  const past = (rides || []).filter((r) => r.date < today || r.status === "cancelled");

  return (
    <div className="min-h-screen grain relative bg-[var(--nl-bg)] px-6 py-10" data-testid="ride-history-page">
      <div className="max-w-3xl mx-auto">
        <Link to="/" className="flex items-baseline gap-1 mb-2 justify-center">
          <span className="font-serif text-2xl">Namarck</span>
          <span className="font-serif text-2xl italic text-gold">Limo</span>
        </Link>
        <p className="label text-gold text-center mb-8">My Rides</p>

        <form onSubmit={submit} className="flex gap-3 mb-10" data-testid="rides-phone-form">
          <input
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="Enter the phone number you booked with"
            className="flex-1 bg-[var(--nl-bg-3)] border border-white/10 px-4 py-3 text-white font-body focus:outline-none focus:border-gold transition-colors duration-300"
            data-testid="rides-phone-input"
          />
          <button type="submit" disabled={loading} className="label bg-gold text-black px-6 hover:bg-[var(--nl-gold-hover)] transition-colors duration-300 disabled:opacity-60" data-testid="rides-load-btn">
            {loading ? "…" : "Find"}
          </button>
        </form>

        {rides !== null && rides.length === 0 && (
          <p className="text-white/40 font-body text-center py-10" data-testid="rides-empty">No rides found for this number.</p>
        )}

        {upcoming.length > 0 && (
          <>
            <h2 className="font-serif font-light text-2xl mb-4">Upcoming</h2>
            <div className="space-y-3 mb-10">{upcoming.map((r) => <RideCard key={r.id} r={r} onRebook={rebook} phone={phone.trim()} />)}</div>
          </>
        )}
        {past.length > 0 && (
          <>
            <h2 className="font-serif font-light text-2xl mb-4">Past</h2>
            <div className="space-y-3">{past.map((r) => <RideCard key={r.id} r={r} onRebook={rebook} phone={phone.trim()} />)}</div>
          </>
        )}

        <Link to="/#book" className="mt-10 block text-center label text-gold border-b border-gold/40 pb-1 w-fit mx-auto hover:border-gold transition-colors duration-300" data-testid="rides-book-btn">
          Book another ride →
        </Link>
      </div>
    </div>
  );
}
